import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment.development';

export interface EventoEnVivo {
  id: number;
  id_curso: number;
  id_maestro: number;
  titulo: string;
  descripcion: string;
  fecha_inicio: string;
  fecha_fin: string;
  url_transmision: string;
  iframe_video: string;
  iframe_chat: string;
  imagen_portada: string | null;
  instructor: string;
  capacidad_maxima: number;
  inscritos: number;
  estado: 'PROGRAMADO' | 'EN_VIVO' | 'FINALIZADO' | 'CANCELADO';
  color: string;
  ubicacion: string;
  notas: string;
  recordatorio_enviado: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EventosResponse {
  ok: boolean;
  data?: EventoEnVivo[];
  total?: number;
  msg?: string;
}

export interface EventoResponse {
  ok?: boolean;
  message?: string;
  msg?: string;
  data?: EventoEnVivo;
}

export interface InscripcionResponse {
  ok: boolean;
  msg?: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class EventosService {
  private readonly API_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene todos los eventos disponibles
   */
  getEventosDisponibles(): Observable<EventosResponse> {
    return this.http.get<EventosResponse>(`${this.API_URL}/eventos/disponibles`).pipe(
      tap(response => {
        console.log('📺 Eventos disponibles:', response);
      }),
      catchError(error => {
        console.error('Error al obtener eventos:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los eventos disponibles'
        });
      })
    );
  }

  /**
   * Obtiene eventos de un curso específico
   */
  getEventosCurso(idCurso: number): Observable<EventosResponse> {
    return this.http.get<EventosResponse>(`${this.API_URL}/eventos/curso/${idCurso}`).pipe(
      tap(response => {
        console.log('📺 Eventos del curso:', response);
      }),
      catchError(error => {
        console.error('Error al obtener eventos del curso:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar los eventos del curso'
        });
      })
    );
  }

  /**
   * Obtiene los eventos en los que el usuario está inscrito
   */
  getMisEventos(): Observable<EventosResponse> {
    return this.http.get<EventosResponse>(`${this.API_URL}/eventos/mis-eventos`).pipe(
      tap(response => {
        console.log('📺 Mis eventos:', response);
      }),
      catchError(error => {
        console.error('Error al obtener mis eventos:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al cargar tus eventos'
        });
      })
    );
  }

  /**
   * Obtiene el detalle de un evento
   */
  getEvento(idEvento: number): Observable<EventoResponse> {
    return this.http.get<EventoResponse>(`${this.API_URL}/eventos/${idEvento}`).pipe(
      tap(response => {
        console.log('📺 Detalle del evento:', response);
      }),
      catchError(error => {
        console.error('Error al obtener evento:', error);
        return of({
          ok: false,
          message: error.error?.message || error.error?.msg || 'Error al cargar el evento'
        });
      })
    );
  }

  /**
   * Inscribe al usuario en un evento
   */
  inscribirseEvento(idEvento: number): Observable<InscripcionResponse> {
    return this.http.post<InscripcionResponse>(`${this.API_URL}/eventos/${idEvento}/inscribir`, {}).pipe(
      tap(response => {
        console.log('✅ Inscripción al evento:', response);
      }),
      catchError(error => {
        console.error('Error al inscribirse:', error);
        return of({
          ok: false,
          msg: error.error?.msg || 'Error al inscribirse en el evento'
        });
      })
    );
  }

  /**
   * Verifica si un evento está en vivo ahora
   */
  isEventoEnVivo(evento: EventoEnVivo): boolean {
    const ahora = new Date();
    const inicio = this.parseAsLocalDate(evento.fecha_inicio);
    const fin = this.parseAsLocalDate(evento.fecha_fin);
    
    return ahora >= inicio && ahora <= fin && evento.estado === 'EN_VIVO';
  }

  /**
   * Verifica si un evento es próximo (en las próximas 24 horas)
   */
  isEventoProximo(evento: EventoEnVivo): boolean {
    const ahora = new Date();
    const inicio = this.parseAsLocalDate(evento.fecha_inicio);
    const diff = inicio.getTime() - ahora.getTime();
    const horasRestantes = diff / (1000 * 60 * 60);
    
    return horasRestantes > 0 && horasRestantes <= 24;
  }

  /**
   * Convierte una fecha UTC string a Date tratándola como hora local
   * Las fechas en la BD vienen con 'Z' pero ya están en hora local de El Salvador
   */
  private parseAsLocalDate(dateString: string): Date {
    // Remover la 'Z' y cualquier indicador de zona horaria
    const cleanDateString = dateString.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    
    // Parsear como fecha local
    const date = new Date(cleanDateString);
    
    return date;
  }

  /**
   * Formatea la duración del evento
   */
  getDuracionEvento(evento: EventoEnVivo): string {
    const inicio = this.parseAsLocalDate(evento.fecha_inicio);
    const fin = this.parseAsLocalDate(evento.fecha_fin);
    const diff = fin.getTime() - inicio.getTime();
    const minutos = Math.floor(diff / (1000 * 60));
    
    if (minutos < 60) {
      return `${minutos} min`;
    }
    
    const horas = Math.floor(minutos / 60);
    const minsRestantes = minutos % 60;
    
    return minsRestantes > 0 ? `${horas}h ${minsRestantes}m` : `${horas}h`;
  }

  /**
   * Formatea la fecha/hora del evento
   */
  formatFechaEvento(fecha: string): string {
    const date = this.parseAsLocalDate(fecha);
    const opciones: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return date.toLocaleDateString('es-ES', opciones);
  }
}

